// Designs section — plans, elevations and renders shared by the retailer.
//
// Source-of-truth references (READ-ONLY):
//   - customer-standalone.html:758-770   "page-designs" panel
//   - customer-live.js:625-649            renderDesigns
//   - retailer/src/pages/designs/page.ts  upload/visibility patterns we mirror
//                                          on the read-side
//
// The customer portal is read-only: the retailer has already chosen what to
// share. Each `DesignFile` carries a fully-resolved `fileUrl` (no per-file
// API call needed for V1) plus optional `notes` and a `fileType`.
//
// Behaviour
// ─────────
//   - Files are sorted by `uploadedAt` desc.
//   - Image files (jpg/jpeg/png/webp) preview inline as <img>; non-image
//     files (pdf/dwg/svg/etc.) render a typed badge tile.
//   - "View" opens the file in a new tab. For images we *also* have a
//     <dialog> lightbox: clicking an image opens it; arrow keys / on-screen
//     prev-next move between images; Escape / clicking the backdrop closes.
//   - Notes (if present) render as italic small text under each tile.
//   - The whole section is hidden upstream when `designs.length === 0`.
//
// Cleanup tears down listeners and closes the dialog. We don't allocate any
// `URL.createObjectURL` blobs (URLs are server-owned), so nothing to revoke.

import type {
  CustomerProject,
  CustomerSection,
  DesignFile,
  SectionCleanup,
  SectionContext,
} from "../types";

import "./designs.css";

// ── Helpers ──────────────────────────────────────────────────────────────────

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "webp", "gif", "avif"]);

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fmtDate(raw: string): string {
  const t = Date.parse(raw);
  if (!Number.isFinite(t)) return "—";
  return DATE_FMT.format(new Date(t));
}

function extOf(file: DesignFile): string {
  if (file.fileType && file.fileType.length > 0) return file.fileType.toLowerCase();
  const dot = file.filename.lastIndexOf(".");
  if (dot < 0 || dot === file.filename.length - 1) return "";
  return file.filename.slice(dot + 1).toLowerCase();
}

function isImage(file: DesignFile): boolean {
  return IMAGE_EXTS.has(extOf(file));
}

function badgeLabel(file: DesignFile): string {
  const ext = extOf(file);
  return ext === "" ? "FILE" : ext.toUpperCase();
}

// ── "Customise my kitchen" panel ───────────────────────────────────────────
// Self-contained: a CTA appended into the designs card, a right-side slide-in
// panel of finish choices, and a polling notification. No AI language anywhere.
// Geometry + door style are intentionally NOT offered (locked by the retailer).

type IterStatus = {
  ok: boolean;
  count: number;
  remaining: number;
  cooldownMsLeft: number;
};

// Curated finish choices — labels are customer-friendly; the values are the
// shared wizard_data enums the render engine understands. Colour is free text
// (the server maps it to the nearest real catalogue colour).
const COLOUR_OPTS: Array<{ label: string; value: string; hex: string }> = [
  { label: "Sage Green", value: "sage green", hex: "#b3bda3" },
  { label: "Forest Green", value: "forest green", hex: "#3f5c46" },
  { label: "Navy", value: "navy blue", hex: "#2b3a5b" },
  { label: "Indigo", value: "indigo", hex: "#4b5377" },
  { label: "Dusky Pink", value: "dusky pink", hex: "#c9a8a2" },
  { label: "Stone", value: "stone", hex: "#c8bfae" },
  { label: "Cashmere", value: "cashmere cream", hex: "#e6dcc6" },
  { label: "Light Grey", value: "light grey", hex: "#c4c6c8" },
  { label: "Dark Grey", value: "anthracite grey", hex: "#54585c" },
  { label: "Classic White", value: "white", hex: "#f3f1ec" },
  { label: "Matt Black", value: "black", hex: "#26282b" },
  { label: "Oak", value: "natural oak", hex: "#b48a5a" },
];
const WORKTOP_OPTS: Array<{ label: string; value: string }> = [
  { label: "White Quartz", value: "white_quartz" },
  { label: "Light Stone", value: "light_stone" },
  { label: "Marble", value: "marble" },
  { label: "Dark Granite", value: "dark_granite" },
  { label: "Black Quartz", value: "black_quartz" },
  { label: "Oak Wood", value: "oak_wood" },
  { label: "Walnut Wood", value: "walnut_wood" },
  { label: "Concrete", value: "concrete" },
];
const HANDLE_OPTS: Array<{ label: string; value: string }> = [
  { label: "Brass Bar", value: "brass_bar" },
  { label: "Chrome Bar", value: "chrome_bar" },
  { label: "Black Bar", value: "black_bar" },
  { label: "Nickel Bar", value: "nickel_bar" },
  { label: "Cup Pull", value: "cup" },
  { label: "Knob", value: "knob" },
  { label: "Handleless", value: "handleless" },
];
const FLOORING_OPTS: Array<{ label: string; value: string }> = [
  { label: "Light Oak", value: "light_oak" },
  { label: "Dark Oak", value: "dark_oak" },
  { label: "Herringbone", value: "herringbone" },
  { label: "Stone Tile", value: "stone_tile" },
  { label: "Porcelain", value: "porcelain" },
  { label: "Concrete", value: "concrete" },
  { label: "Vinyl Plank", value: "lvt" },
];

function mountCustomise(
  card: HTMLElement,
  container: HTMLElement,
  ctx: SectionContext,
): () => void {
  // Override key → selected value. Only changed groups are sent.
  const sel: Record<string, string> = {};

  // ── CTA inside the designs card ───────────────────────────────────────────
  const cta = document.createElement("div");
  cta.className = "des-customise-cta";
  cta.innerHTML = `
    <div class="des-customise-cta-text">
      <strong>Want to see it your way?</strong>
      <span>Try different colours, worktops, handles and flooring — your layout stays exactly as designed.</span>
    </div>
    <button type="button" class="cust-btn cust-btn-primary" data-role="customise-open">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3v3m0 12v3M3 12h3m12 0h3M5.6 5.6l2.1 2.1m8.6 8.6 2.1 2.1m0-12.8-2.1 2.1m-8.6 8.6-2.1 2.1" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" stroke-width="1.5"/>
      </svg>
      Customise my kitchen
    </button>
  `;
  card.appendChild(cta);

  // ── Slide-in panel + backdrop ─────────────────────────────────────────────
  const backdrop = document.createElement("div");
  backdrop.className = "des-customise-backdrop";
  backdrop.hidden = true;

  const panel = document.createElement("aside");
  panel.className = "des-customise-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-label", "Customise my kitchen");
  panel.hidden = true;

  const group = (
    title: string,
    key: string,
    opts: Array<{ label: string; value: string; hex?: string }>,
  ): string => `
    <fieldset class="des-cz-group" data-key="${key}">
      <legend>${esc(title)}</legend>
      <div class="des-cz-opts">
        ${opts
          .map(
            (o) => `
          <button type="button" class="des-cz-opt" data-key="${key}" data-value="${esc(o.value)}">
            ${o.hex ? `<span class="des-cz-swatch" style="background:${esc(o.hex)}"></span>` : ""}
            <span class="des-cz-opt-label">${esc(o.label)}</span>
          </button>`,
          )
          .join("")}
      </div>
    </fieldset>
  `;

  panel.innerHTML = `
    <header class="des-cz-head">
      <h3>Customise my kitchen</h3>
      <button type="button" class="des-cz-close" data-role="customise-close" aria-label="Close">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    </header>
    <p class="des-cz-intro">Pick the finishes you'd like to see. Your kitchen's layout, units and door style stay exactly as your retailer designed them.</p>
    <div class="des-cz-body">
      ${group("Door colour", "door_colour", COLOUR_OPTS)}
      ${group("Worktop", "worktop_material", WORKTOP_OPTS)}
      ${group("Handles", "handle_style", HANDLE_OPTS)}
      ${group("Flooring", "flooring_type", FLOORING_OPTS)}
    </div>
    <footer class="des-cz-foot">
      <p class="des-cz-status" data-role="customise-status"></p>
      <button type="button" class="cust-btn cust-btn-primary des-cz-submit" data-role="customise-submit" disabled>
        Create my new look
      </button>
      <p class="des-cz-remaining" data-role="customise-remaining"></p>
    </footer>
  `;

  container.appendChild(backdrop);
  container.appendChild(panel);

  const statusEl = panel.querySelector<HTMLElement>("[data-role='customise-status']")!;
  const submitEl = panel.querySelector<HTMLButtonElement>("[data-role='customise-submit']")!;
  const remainingEl = panel.querySelector<HTMLElement>("[data-role='customise-remaining']")!;

  let status: IterStatus = { ok: true, count: 0, remaining: 5, cooldownMsLeft: 0 };
  let busy = false;
  let pollTimer: number | null = null;
  let cooldownTimer: number | null = null;
  const ac = new AbortController();

  function renderRemaining(): void {
    if (status.cooldownMsLeft > 0) {
      const mins = Math.ceil(status.cooldownMsLeft / 60000);
      remainingEl.textContent = `Please wait about ${mins} minute${mins === 1 ? "" : "s"} before your next look.`;
    } else if (status.remaining <= 0) {
      remainingEl.textContent = "You've used all your looks for now — your retailer can unlock more any time.";
    } else {
      remainingEl.textContent = `${status.remaining} look${status.remaining === 1 ? "" : "s"} remaining.`;
    }
    refreshSubmitState();
  }

  function refreshSubmitState(): void {
    const hasPick = Object.keys(sel).length > 0;
    const blocked = busy || status.remaining <= 0 || status.cooldownMsLeft > 0;
    submitEl.disabled = !hasPick || blocked;
  }

  async function loadStatus(): Promise<void> {
    try {
      const r = await ctx.api<IterStatus>("/customer/render/iterations", { signal: ac.signal });
      if (r && r.ok) {
        status = r;
        renderRemaining();
      }
    } catch {
      /* non-fatal — panel still usable, server will gate on submit */
    }
  }

  function startCooldownTick(): void {
    if (cooldownTimer) window.clearInterval(cooldownTimer);
    cooldownTimer = window.setInterval(() => {
      if (status.cooldownMsLeft > 0) {
        status.cooldownMsLeft = Math.max(0, status.cooldownMsLeft - 1000);
        renderRemaining();
        if (status.cooldownMsLeft === 0 && cooldownTimer) {
          window.clearInterval(cooldownTimer);
          cooldownTimer = null;
        }
      }
    }, 1000);
  }

  function openPanel(): void {
    backdrop.hidden = false;
    panel.hidden = false;
    // Force reflow so the slide-in transition runs.
    void panel.offsetWidth;
    backdrop.classList.add("is-open");
    panel.classList.add("is-open");
    void loadStatus();
  }

  function closePanel(): void {
    backdrop.classList.remove("is-open");
    panel.classList.remove("is-open");
    window.setTimeout(() => {
      backdrop.hidden = true;
      panel.hidden = true;
    }, 280);
  }

  // In-portal notification banner (no email). Shown while a look is being made.
  function showProcessingBanner(): void {
    let banner = container.querySelector<HTMLElement>(".des-customise-banner");
    if (!banner) {
      banner = document.createElement("div");
      banner.className = "des-customise-banner";
      container.insertBefore(banner, container.firstChild);
    }
    banner.innerHTML = `
      <span class="des-customise-spinner" aria-hidden="true"></span>
      <span>We're creating your new look — it'll appear in your designs in a minute or two. You can keep browsing.</span>
    `;
    banner.classList.add("is-visible");
  }

  function clearProcessingBanner(): void {
    const banner = container.querySelector<HTMLElement>(".des-customise-banner");
    if (banner) banner.classList.remove("is-visible");
  }

  // After submitting, poll the project until the design count grows, then
  // refresh the gallery so the new renders appear.
  function startPollingForNewRenders(): void {
    const baseline = (ctx.project.designs || []).length;
    let tries = 0;
    if (pollTimer) window.clearInterval(pollTimer);
    pollTimer = window.setInterval(async () => {
      tries += 1;
      try {
        await ctx.refresh();
      } catch {
        /* ignore transient refresh errors */
      }
      const now = (ctx.project.designs || []).length;
      if (now > baseline) {
        if (pollTimer) window.clearInterval(pollTimer);
        pollTimer = null;
        clearProcessingBanner();
        ctx.showToast("Your new look is ready — see it in your designs.", "success");
      } else if (tries >= 20) {
        // ~2 min ceiling — stop polling; the renders still land server-side and
        // will show on the next visit.
        if (pollTimer) window.clearInterval(pollTimer);
        pollTimer = null;
        clearProcessingBanner();
      }
    }, 6000);
  }

  async function submit(): Promise<void> {
    if (busy || submitEl.disabled) return;
    const overrides: Record<string, string> = { ...sel };
    if (Object.keys(overrides).length === 0) return;
    busy = true;
    refreshSubmitState();
    statusEl.textContent = "Creating your new look…";
    try {
      const r = await ctx.api<{ ok: boolean; remaining?: number; error?: string; cooldownMsLeft?: number }>(
        "/customer/render/iterate",
        { method: "POST", signal: ac.signal, body: JSON.stringify({ overrides }) },
      );
      if (r && r.ok) {
        if (typeof r.remaining === "number") status.remaining = r.remaining;
        statusEl.textContent = "";
        showProcessingBanner();
        startPollingForNewRenders();
        closePanel();
        // Force a short cooldown locally so they can't re-spam before reload.
        status.cooldownMsLeft = 10 * 60 * 1000;
        startCooldownTick();
        renderRemaining();
      } else {
        statusEl.textContent = friendlyError(r && r.error);
      }
    } catch (err) {
      const e = err as ApiErrorLike;
      if (e && e.status === 429) {
        statusEl.textContent = "Please wait a little while before your next look.";
      } else if (e && e.status === 403) {
        statusEl.textContent = "You've used all your looks for now — your retailer can unlock more.";
      } else {
        statusEl.textContent = "Something went wrong — please try again in a moment.";
      }
    } finally {
      busy = false;
      refreshSubmitState();
    }
  }

  function friendlyError(code?: string): string {
    switch (code) {
      case "limit_reached":
        return "You've used all your looks for now — your retailer can unlock more.";
      case "cooldown":
        return "Please wait a little while before your next look.";
      case "no_base_design":
        return "We couldn't find your kitchen details — please contact your retailer.";
      default:
        return "Something went wrong — please try again in a moment.";
    }
  }

  // ── Event wiring ──────────────────────────────────────────────────────────
  const onCtaClick = (e: Event): void => {
    if ((e.target as HTMLElement).closest("[data-role='customise-open']")) openPanel();
  };
  cta.addEventListener("click", onCtaClick);

  const onBackdropClick = (): void => closePanel();
  backdrop.addEventListener("click", onBackdropClick);

  const onPanelClick = (e: Event): void => {
    const t = e.target as HTMLElement;
    if (t.closest("[data-role='customise-close']")) {
      closePanel();
      return;
    }
    if (t.closest("[data-role='customise-submit']")) {
      void submit();
      return;
    }
    const opt = t.closest<HTMLButtonElement>(".des-cz-opt");
    if (opt) {
      const key = opt.dataset["key"]!;
      const value = opt.dataset["value"]!;
      // Toggle: clicking the selected option again clears that group.
      const groupEl = opt.closest(".des-cz-group");
      if (sel[key] === value) {
        delete sel[key];
        opt.classList.remove("is-selected");
      } else {
        sel[key] = value;
        groupEl
          ?.querySelectorAll(".des-cz-opt.is-selected")
          .forEach((el) => el.classList.remove("is-selected"));
        opt.classList.add("is-selected");
      }
      refreshSubmitState();
    }
  };
  panel.addEventListener("click", onPanelClick);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && !panel.hidden) closePanel();
  };
  document.addEventListener("keydown", onKey);

  return () => {
    ac.abort();
    if (pollTimer) window.clearInterval(pollTimer);
    if (cooldownTimer) window.clearInterval(cooldownTimer);
    cta.removeEventListener("click", onCtaClick);
    backdrop.removeEventListener("click", onBackdropClick);
    panel.removeEventListener("click", onPanelClick);
    document.removeEventListener("keydown", onKey);
    if (cta.parentNode) cta.parentNode.removeChild(cta);
    if (backdrop.parentNode === container) container.removeChild(backdrop);
    if (panel.parentNode === container) container.removeChild(panel);
    const banner = container.querySelector(".des-customise-banner");
    if (banner && banner.parentNode === container) container.removeChild(banner);
  };
}

type ApiErrorLike = { status?: number } | null;

// ── Section ──────────────────────────────────────────────────────────────────

// AI-generated renders live under /uploads/renders/. When the global
// customer-render feature flag is off we hide ALL render content — the tiles,
// the AI note and the "Customise my kitchen" panel (which keys off hasAiRenders).
const isRenderFile = (d: DesignFile): boolean =>
  typeof d.fileUrl === "string" && d.fileUrl.includes("/uploads/renders/");

const visibleDesigns = (project: CustomerProject): DesignFile[] => {
  const list = Array.isArray(project.designs) ? project.designs : [];
  return project.customerRenderEnabled === true ? list : list.filter((d) => !isRenderFile(d));
};

const designsSection: CustomerSection = {
  id: "designs",

  shouldRender(project: CustomerProject): boolean {
    return visibleDesigns(project).length > 0;
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    let disposed = false;

    // Sorted copy — newest first. Render content is filtered out entirely when
    // the global customer-render feature flag is off (visibleDesigns()).
    const designs: DesignFile[] = visibleDesigns(ctx.project)
      .slice()
      .sort((a, b) => {
        const ta = Date.parse(a.uploadedAt) || 0;
        const tb = Date.parse(b.uploadedAt) || 0;
        return tb - ta;
      });

    // Index of each design in the sorted list — used by the lightbox.
    const imageDesigns: DesignFile[] = designs.filter(isImage);
    const imageIndexById = new Map<string, number>();
    imageDesigns.forEach((d, i) => imageIndexById.set(d.id, i));

    // ── Card ────────────────────────────────────────────────────────────────

    const card = document.createElement("section");
    card.className = "cust-card des-card";
    card.setAttribute("aria-labelledby", "des-title");

    // Subtle note above the gallery whenever AI-generated renders are present.
    const hasAiRenders = designs.some(
      (d) => typeof d.fileUrl === "string" && d.fileUrl.includes("/uploads/renders/"),
    );
    const aiNote = hasAiRenders
      ? `<p class="des-ai-note">Your kitchen vision is generated from the details you provided. It's designed to inspire — your retailer will refine the exact design with you.</p>`
      : "";

    card.innerHTML = `
      <header class="des-head">
        <h2 id="des-title" class="cust-card-title">Your designs</h2>
        <p class="des-subtitle">Plans, elevations and renders shared by your retailer</p>
      </header>
      ${aiNote}
      <div class="des-grid" data-role="grid"></div>
    `;

    const gridEl = card.querySelector<HTMLDivElement>("[data-role='grid']")!;

    for (const d of designs) {
      gridEl.appendChild(renderTile(d));
    }

    container.appendChild(card);

    // ── "Customise my kitchen" — customer self-iteration ─────────────────────
    // Only offered when the retailer has shared AI-generated renders. Lets the
    // customer try different finishes; the layout and door style stay fixed
    // (the retailer designed those with them). New looks appear in this gallery.
    let customiseCleanup: (() => void) | null = null;
    if (hasAiRenders) customiseCleanup = mountCustomise(card, container, ctx);

    // ── Lightbox ────────────────────────────────────────────────────────────

    const dialog = document.createElement("dialog");
    dialog.className = "des-lightbox";
    dialog.innerHTML = `
      <div class="des-lightbox-frame">
        <button type="button" class="des-lightbox-close" data-role="close" aria-label="Close">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <button type="button" class="des-lightbox-nav des-lightbox-prev" data-role="prev" aria-label="Previous design">
          <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="15 18 9 12 15 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <img class="des-lightbox-img" data-role="img" alt="" />
        <button type="button" class="des-lightbox-nav des-lightbox-next" data-role="next" aria-label="Next design">
          <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="9 6 15 12 9 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <figcaption class="des-lightbox-caption" data-role="caption"></figcaption>
      </div>
    `;
    container.appendChild(dialog);

    const lightboxImg = dialog.querySelector<HTMLImageElement>("[data-role='img']")!;
    const lightboxCaption = dialog.querySelector<HTMLElement>("[data-role='caption']")!;
    const closeBtn = dialog.querySelector<HTMLButtonElement>("[data-role='close']")!;
    const prevBtn = dialog.querySelector<HTMLButtonElement>("[data-role='prev']")!;
    const nextBtn = dialog.querySelector<HTMLButtonElement>("[data-role='next']")!;

    let currentImageIdx = -1;

    // Tell the backend the customer has opened this design so the retailer sees
    // a "Viewed" indicator (mirrors quote "Seen"). Fire-and-forget, once per id.
    const viewedSent = new Set<string>();
    function markViewed(id: string): void {
      if (!id || viewedSent.has(id)) return;
      viewedSent.add(id);
      try {
        void fetch(`/api/customer/designs/${encodeURIComponent(id)}/viewed`, {
          method: "POST",
          credentials: "include",
          keepalive: true,
        }).catch(() => { /* best-effort */ });
      } catch { /* ignore */ }
    }

    function openLightbox(idx: number): void {
      if (disposed) return;
      if (idx < 0 || idx >= imageDesigns.length) return;
      currentImageIdx = idx;
      const d = imageDesigns[idx];
      if (!d) return;
      markViewed(d.id);
      lightboxImg.src = d.fileUrl;
      lightboxImg.alt = d.filename;
      lightboxCaption.textContent = `${d.filename}${d.notes ? ` — ${d.notes}` : ""}`;
      const showNav = imageDesigns.length > 1;
      prevBtn.hidden = !showNav;
      nextBtn.hidden = !showNav;
      if (typeof dialog.showModal === "function") {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
    }

    function closeLightbox(): void {
      if (dialog.open) dialog.close();
      lightboxImg.removeAttribute("src");
      currentImageIdx = -1;
    }

    function step(delta: number): void {
      if (imageDesigns.length === 0 || currentImageIdx < 0) return;
      const next = (currentImageIdx + delta + imageDesigns.length) % imageDesigns.length;
      openLightbox(next);
    }

    // ── Tile renderer ───────────────────────────────────────────────────────

    function renderTile(d: DesignFile): HTMLElement {
      const tile = document.createElement("article");
      tile.className = "des-tile";
      tile.dataset.designId = d.id;

      const dateText = fmtDate(d.uploadedAt);
      const badge = badgeLabel(d);
      const isImg = isImage(d);
      const safeName = esc(d.filename);

      const thumbHtml = isImg
        ? `<img class="des-tile-img" src="${esc(d.fileUrl)}" alt="${safeName}" loading="lazy" />`
        : `<div class="des-tile-placeholder" data-ext="${esc(extOf(d) || "file")}">
             <svg viewBox="0 0 24 24" aria-hidden="true">
               <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" stroke-width="1.5"/>
               <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" stroke-width="1.5"/>
             </svg>
             <span>${esc(badge)}</span>
           </div>`;

      tile.innerHTML = `
        <div class="des-tile-thumb" data-role="${isImg ? "thumb-image" : "thumb-file"}">
          ${thumbHtml}
          <span class="des-tile-badge">${esc(badge)}</span>
        </div>
        <div class="des-tile-body">
          <div class="des-tile-name" title="${safeName}">${safeName}</div>
          <div class="des-tile-meta">${esc(dateText)}</div>
          ${
            d.notes && d.notes.trim() !== ""
              ? `<p class="des-tile-note">${esc(d.notes.trim())}</p>`
              : ""
          }
        </div>
        <div class="des-tile-actions">
          <a class="cust-btn-secondary des-view-btn"
             href="${esc(d.fileUrl)}"
             target="_blank"
             rel="noopener noreferrer"
             data-role="view"
             data-is-image="${isImg ? "1" : "0"}">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="none" stroke="currentColor" stroke-width="1.6"/>
              <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.6"/>
            </svg>
            View
          </a>
        </div>
      `;

      return tile;
    }

    // ── Wiring ──────────────────────────────────────────────────────────────

    const onGridClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const viewLink = target.closest<HTMLAnchorElement>(
        "[data-role='view']",
      );
      // Non-image files (PDF / DWG / docx renders) open in a new tab — still a
      // view, so record it before the default navigation proceeds.
      if (viewLink && viewLink.dataset.isImage !== "1") {
        const id = viewLink.closest<HTMLElement>(".des-tile")?.dataset.designId;
        if (id) markViewed(id);
        return;
      }
      if (viewLink && viewLink.dataset.isImage === "1") {
        e.preventDefault();
        const tile = viewLink.closest<HTMLElement>(".des-tile");
        const id = tile?.dataset.designId;
        if (!id) {
          // Fall back to opening in a new tab if we somehow lost the id.
          window.open(viewLink.href, "_blank", "noopener,noreferrer");
          return;
        }
        const idx = imageIndexById.get(id);
        if (idx === undefined) {
          window.open(viewLink.href, "_blank", "noopener,noreferrer");
          return;
        }
        openLightbox(idx);
        return;
      }
      // Also allow clicking the image thumb itself to open the lightbox.
      const imgThumb = target.closest<HTMLElement>("[data-role='thumb-image']");
      if (imgThumb) {
        const tile = imgThumb.closest<HTMLElement>(".des-tile");
        const id = tile?.dataset.designId;
        if (!id) return;
        const idx = imageIndexById.get(id);
        if (idx === undefined) return;
        openLightbox(idx);
      }
    };
    gridEl.addEventListener("click", onGridClick);

    const onCloseClick = (): void => closeLightbox();
    closeBtn.addEventListener("click", onCloseClick);

    const onPrevClick = (): void => step(-1);
    const onNextClick = (): void => step(1);
    prevBtn.addEventListener("click", onPrevClick);
    nextBtn.addEventListener("click", onNextClick);

    // Click backdrop to close.
    const onDialogClick = (e: MouseEvent): void => {
      if (e.target === dialog) closeLightbox();
    };
    dialog.addEventListener("click", onDialogClick);

    // Arrow keys while lightbox open.
    const onKey = (e: KeyboardEvent): void => {
      if (!dialog.open) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        step(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        step(1);
      }
      // Escape is handled natively by <dialog>.
    };
    document.addEventListener("keydown", onKey);

    // Native <dialog> close (Esc / form submit) — clear src.
    const onDialogClose = (): void => {
      lightboxImg.removeAttribute("src");
      currentImageIdx = -1;
    };
    dialog.addEventListener("close", onDialogClose);

    // ── Cleanup ─────────────────────────────────────────────────────────────

    return () => {
      disposed = true;
      if (customiseCleanup) customiseCleanup();
      gridEl.removeEventListener("click", onGridClick);
      closeBtn.removeEventListener("click", onCloseClick);
      prevBtn.removeEventListener("click", onPrevClick);
      nextBtn.removeEventListener("click", onNextClick);
      dialog.removeEventListener("click", onDialogClick);
      dialog.removeEventListener("close", onDialogClose);
      document.removeEventListener("keydown", onKey);
      if (dialog.open) dialog.close();
      if (dialog.parentNode === container) container.removeChild(dialog);
      if (card.parentNode === container) container.removeChild(card);
    };
  },
};

export default designsSection;
